import {useState, useEffect} from "react";
import {motion} from "framer-motion";
import {successMessages, failureMessages} from "./messages";

const spinSound = new Audio('/sounds/spin.mp3');
const winSound = new Audio('/sounds/win.mp3');

const symbolValues: { [key: string]: number } = {
    "🍒": 10,
    "🍋": 20,
    "🍉": 30,
    "🍇": 40,
    "🍍": 50,
    "🍓": 60,
    "💎": 100,
};

const symbols = Object.keys(symbolValues);
const ROWS = 3;
const REELS = 5;
const STARTING_BALANCE = 500; // initial money on page load
const BET_AMOUNT = 50; // money deducted per spin


const getRandomSymbols = () =>
    Array.from({length: ROWS}, () =>
        Array.from({length: REELS}, () => symbols[Math.floor(Math.random() * symbols.length)])
    );

export default function SlotMachine() {
    const [soundOn, setSoundOn] = useState(true);
    const [grid, setGrid] = useState(getRandomSymbols());
    const [currentGrid, setCurrentGrid] = useState(getRandomSymbols());
    const [spinning, setSpinning] = useState(false);
    const [stoppedReels, setStoppedReels] = useState(0);
    const [messages, setMessages] = useState<string[]>([]);
    const [rowScores, setRowScores] = useState<number[]>([]);
    const [outcomeHistory, setOutcomeHistory] = useState<
        { message: string; time: string; result: string[][] }[]
    >([]);
    const [, setGameCount] = useState(0);
    const [showEncouragingMessage, setShowEncouragingMessage] = useState(false);
    const [totalScore, setTotalScore] = useState(STARTING_BALANCE);
    const [bestSpin, setBestSpin] = useState(0);
    const [winningPositions, setWinningPositions] = useState<Set<string>>(new Set());
    const [showGameOver, setShowGameOver] = useState(false);

    const resetGame = () => {
        setTotalScore(STARTING_BALANCE);
        setGrid(getRandomSymbols());
        setCurrentGrid(getRandomSymbols());
        setSpinning(false);
        setStoppedReels(0);
        setMessages([]);
        setRowScores([]);
        setOutcomeHistory([]);
        setShowGameOver(false);
    };

    const spin = () => {
        if (totalScore < BET_AMOUNT) {
            setShowGameOver(true);
            return;
        }

        if (soundOn) {
            spinSound.currentTime = 0;
            spinSound.loop = true; // Loop the spin sound
            spinSound.play().catch((err) => {
                console.error("Spin sound failed to play:", err);
            })
        }

        setTotalScore(prev => prev - BET_AMOUNT);
        setGameCount((prevCount) => {
            let newCount = prevCount + 1;
            if (newCount === 30) {
                setShowEncouragingMessage(true);
                setTimeout(() => setShowEncouragingMessage(false), 8000);
                newCount = 0;
            }
            return newCount;
        });

        setSpinning(true);
        setStoppedReels(0);
        setMessages([]);
        setRowScores([]);
        setWinningPositions(new Set()); // ✅ reset highlights

        const newGrid = getRandomSymbols();
        setGrid(newGrid);

        setTimeout(() => {
            let reelsStopped = 0;
            const stopInterval = setInterval(() => {
                reelsStopped++;
                setStoppedReels(reelsStopped);
                if (reelsStopped === REELS) {
                    clearInterval(stopInterval);
                    setSpinning(false);
                    evaluateWin(newGrid);
                    if (soundOn) {
                        spinSound.pause();
                        spinSound.currentTime = 0;
                    }
                }
            }, 500);
        }, 3000);
    };



    // Animate spinning
    useEffect(() => {
        if (spinning) {
            const interval = setInterval(() => {
                setCurrentGrid((prev) =>
                    prev.map((row, rowIdx) =>
                        row.map((_, colIdx) =>
                            colIdx >= stoppedReels // reels still spinning on the right side
                                ? symbols[Math.floor(Math.random() * symbols.length)]
                                : grid[rowIdx][colIdx]
                        )
                    )
                );
            }, 100);
            return () => clearInterval(interval);
        } else {
            setCurrentGrid(grid);
        }
    }, [spinning, grid, stoppedReels]);

    const evaluateWin = (resultGrid: string[][]) => {
        const rowScores: number[] = [];
        let scoreThisRound = 0;
        const wins: Set<string> = new Set();

        // --- 1. Horizontal Wins ---
        for (let row = 0; row < ROWS; row++) {
            const first = resultGrid[row][0];
            if (resultGrid[row].every((s) => s === first)) {
                rowScores.push(symbolValues[first]);
                scoreThisRound += symbolValues[first];
                for (let col = 0; col < REELS; col++) wins.add(`${row}-${col}`);
            }
        }

        // --- 2. Vertical Wins ---
        for (let col = 0; col < REELS; col++) {
            const first = resultGrid[0][col];
            if (resultGrid.every((row) => row[col] === first)) {
                rowScores.push(symbolValues[first]);
                scoreThisRound += symbolValues[first];
                for (let row = 0; row < ROWS; row++) wins.add(`${row}-${col}`);
            }
        }

        // --- 3. Diagonals ---
        const diag1First = resultGrid[0][0];
        if (resultGrid.every((row, i) => row[i] === diag1First)) {
            rowScores.push(symbolValues[diag1First]);
            scoreThisRound += symbolValues[diag1First];
            for (let i = 0; i < ROWS; i++) wins.add(`${i}-${i}`);
        }

        const diag2First = resultGrid[0][REELS - 1];
        if (resultGrid.every((row, i) => row[REELS - 1 - i] === diag2First)) {
            rowScores.push(symbolValues[diag2First]);
            scoreThisRound += symbolValues[diag2First];
            for (let i = 0; i < ROWS; i++) wins.add(`${i}-${REELS - 1 - i}`);
        }

        // --- 4. Three-in-a-Row ---
        for (let row = 0; row < ROWS; row++) {
            for (let col = 0; col <= REELS - 3; col++) {
                const slice = resultGrid[row].slice(col, col + 3);
                if (slice.every((s) => s === slice[0])) {
                    const partialWin = symbolValues[slice[0]] / 2;
                    rowScores.push(partialWin);
                    scoreThisRound += partialWin;
                    for (let c = col; c < col + 3; c++) wins.add(`${row}-${c}`);
                }
            }
        }

        // --- 5. Scatter 💎 ---
        const allSymbols = resultGrid.flat();
        const diamonds = allSymbols.filter((s) => s === "💎").length;
        if (diamonds >= 3) {
            rowScores.push(200);
            scoreThisRound += 200;
            resultGrid.forEach((row, r) =>
                row.forEach((s, c) => {
                    if (s === "💎") wins.add(`${r}-${c}`);
                })
            );
        }

        // --- Pick a message ---
        let roundMessage = "";
        if (scoreThisRound > 0) {
            if (soundOn) {
                winSound.currentTime = 0;
                winSound.play().catch((err) => {console.error("Win sound failed to play:", err);});
            }
            roundMessage = successMessages[Math.floor(Math.random() * successMessages.length)];
        } else {
            roundMessage = failureMessages[Math.floor(Math.random() * failureMessages.length)];
        }

        // --- Save outcome ---
        const currentTime = new Date().toLocaleTimeString();
        setOutcomeHistory((prev) => [
            {
                message: roundMessage,
                time: currentTime,
                result: resultGrid,
            },
            ...prev,
        ]);

        const totalWin = rowScores.reduce((a, b) => a + b, 0);
        setTotalScore(prev => prev + totalWin);
        setMessages([roundMessage]);
        setRowScores(rowScores);
        setBestSpin((prev) => Math.max(prev, scoreThisRound));
        setWinningPositions(wins);
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen">
            {/* Sound Toggle Button */}
            <div className="absolute top-4 left-4 z-50">
                <button
                    onClick={() => {
                        setSoundOn(s => !s)
                        spinSound.pause();
                        winSound.pause();
                        }}
                    className={`bg-yellow-300 border-2 border-yellow-400 rounded-full px-4 py-2 shadow-lg font-bold flex items-center space-x-2 ${
                        soundOn ? "text-yellow-700" : "text-gray-400"
                    }`}
                    aria-label={soundOn ? "Mute sound" : "Unmute sound"}
                >
                    {soundOn ? "🎰 Zvuk zapnutý (Kasíno fičí!)" : "🔇 Ticho v kasíne (Krupiér spí)"}
                </button>
            </div>
            {/* Main Content (Left Screen) */}
            <div
                className="flex-[1] md:basis-[70%] flex flex-col items-center justify-center bg-brown-light p-4 md:p-8"
            >
                <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-8 text-brown-dark">
                    Toč ovocka
                </h1>

                {/* ✅ Score Section */}
                <div className="mb-4 flex flex-col items-center space-y-2">
                    <div className="text-xl md:text-2xl font-bold text-green-700">
                        Celkové skóre: {totalScore}
                    </div>
                    <div className="text-lg md:text-xl font-bold text-blue-700">
                        Najlepšia otočka: {bestSpin}
                    </div>
                </div>

                <div className="p-4 md:p-8 bg-white shadow-lg rounded-lg mb-4 md:mb-8">
                    <div className="rounded-lg flex flex-col space-y-2 md:space-y-4">
                        {Array.from({ length: ROWS }).map((_, rowIdx) => (
                            <div
                                key={rowIdx}
                                className="flex justify-center space-x-2 md:space-x-4 text-4xl md:text-6xl font-bold text-brown-dark"
                            >
                                {Array.from({ length: REELS }).map((_, colIdx) => {
                                    const isStopped = colIdx < stoppedReels;
                                    const isWinning = winningPositions.has(`${rowIdx}-${colIdx}`);
                                    return (
                                        <motion.span
                                            key={colIdx}
                                            animate={
                                                spinning && !isStopped
                                                    ? { y: [0, -50, 0] }
                                                    : { y: 0 }
                                            }
                                            transition={
                                                spinning && !isStopped
                                                    ? { duration: 0.5, repeat: Infinity }
                                                    : { duration: 0 }
                                            }
                                            className={`px-2 rounded ${
                                                isWinning ? "bg-yellow-300 shadow-lg scale-110" : ""
                                            }`}
                                        >
                                            {currentGrid[rowIdx][colIdx]}
                                        </motion.span>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
                <motion.div
                    onClick={!spinning ? spin : undefined}
                    whileHover={{scale: 1.2}}
                    whileTap={{scale: 0.95}}
                    className={`inline-block cursor-pointer bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-lg md:text-xl font-bold px-6 py-3 rounded-2xl hover:from-orange-500 hover:to-yellow-400 transition-all duration-300 shadow-2xl ${
                        spinning ? "opacity-50 pointer-events-none" : ""
                    }`}
                >
                    Hraj!
                </motion.div>
                <div
                    className="mt-4 md:mt-6 text-2xl md:text-3xl h-32 flex flex-col items-center justify-center font-bold">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={rowScores[idx] > 0 ? "text-green-700" : "text-red-500"}>
                            {msg}
                            {rowScores[idx] > 0 && (
                                <span className="ml-2 text-xl md:text-2xl">
                                    (+{rowScores[idx]} evricok)
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Previous Outcomes (Right Screen) */}
            <div className="flex-[1] md:flex-grow flex flex-col bg-brown-dark p-4 text-brown-light">
                <h2 className="font-bold mb-2">Jak si dopadol:</h2>
                <div className="flex flex-col gap-2 md:gap-4">
                    {outcomeHistory.map((outcome, index) => (
                        <div key={index} className="text-xs md:text-sm">
                            <strong>{outcome.time}</strong>
                            <div>
                                {outcome.result.map((row, i) => (
                                    <div key={i}>{row.join(" ")}</div>
                                ))}
                            </div>
                            - {outcome.message}
                        </div>
                    ))}
                </div>
            </div>

            {/* Encouraging Message Box */}
            {showEncouragingMessage && (
                <div
                    className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-700 text-white text-sm md:text-lg font-bold px-4 py-2 rounded-lg shadow-lg flex flex-col items-center justify-center">
                    <p>Gratulujem! Odohral si 30 hier! Pokračuj, veď generačné bohatstvo sa samé nestvorí.</p>
                    <a
                        href="https://www.urhh.sk/linka-pomoci-pre-problemy-s-hranim"
                        className="underline text-blue-300 font-bold bg-blue-800 px-2 py-1 rounded mt-2"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Tu nájdeš skutočnú pomoc
                    </a>
                </div>
            )}
            {/* game over popup */}
            {showGameOver && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                        <h2 className="text-2xl font-bold mb-4">Prehral si!</h2>
                        <p className="mb-4">Nemáš dosť peňazí na ďalší spin. Začni od začiatku.</p>
                        <button
                            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                            onClick={resetGame}
                        >
                            Začať odznova
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}